import { useMemo } from 'react';
import { MacroStep } from './types';

export type AreaProgress = {
  areaFirstStep: number;
  areaLastStep: number;
  areaStepsCount: number;
  completedCount: number;
  areaCompleted: boolean;
  percent: number;
  areaFlexColumn: number;
  isAreaActivated: boolean;
};

type UseWizardProgressProps = {
  stepsConfig: MacroStep[];
  activeStep: number;
};

/**
 * Calculates the progress of each area in a wizard-like step configuration.
 *
 * @param {MacroStep[]} stepsConfig - An array of step configurations, where each area contains its steps.
 * @param {number} activeStep - The index of the currently active step.
 * @returns {AreaProgress[]} An array of progress objects for each area, detailing the progress and state of each area.
 *
 * Each `AreaProgress` object contains:
 * - `areaFirstStep` (number): The index of the first step in the area.
 * - `areaLastStep` (number): The index of the last step in the area.
 * - `areaStepsCount` (number): The total number of steps in the area.
 * - `completedCount` (number): The number of steps completed in the area.
 * - `areaCompleted` (boolean): Whether the area is fully completed.
 * - `percent` (number): The percentage of completion for the area.
 * - `areaFlexColumn` (number): The flex value for the area column, based on the number of steps.
 * - `isAreaActivated` (boolean): Whether the area is currently active or has been activated.
 *
 * Dependencies:
 * - React's `useMemo` is used to memoize the calculation for performance optimization.
 *
 * Example usage:
 * ```javascript
 * import { useWizardProgress } from './useWizardProgress';
 *
 * const stepsConfig = [
 *   { label: 'Step 1', steps: [
 *       { label: 'Colors' },
 *       { label: 'Notifications' },
 *       { label: 'Role' },
 *       { label: 'Extra Options' },
 *   ]},
 *   { label: 'Step 2', ... },
 *   { label: 'Step 3', ... },
 * ];
 * const activeStep = 3;
 *
 * const progress = useWizardProgress({ stepsConfig, activeStep });
 * console.log(progress);
 * // Output:
 * // [
 * //   { areaFirstStep: 0, areaLastStep: 1, areaStepsCount: 2, completedCount: 2, areaCompleted: true, percent: 100, areaFlexColumn: 2, isAreaActivated: true },
 * //   { areaFirstStep: 2, areaLastStep: 2, areaStepsCount: 1, completedCount: 1, areaCompleted: true, percent: 100, areaFlexColumn: 1, isAreaActivated: true },
 * //   { areaFirstStep: 3, areaLastStep: 5, areaStepsCount: 3, completedCount: 1, areaCompleted: false, percent: 0, areaFlexColumn: 3, isAreaActivated: true },
 * // ]
 * ```
 */
export function useWizardProgress({
  stepsConfig,
  activeStep,
}: UseWizardProgressProps): AreaProgress[] {
  return useMemo(() => {
    let stepCount = 0;
    return stepsConfig.map(area => {
      const areaFirstStep = stepCount;
      const areaStepsCount = area.steps.length;
      const areaLastStep = areaFirstStep + areaStepsCount - 1;

      const completedCount =
        activeStep > areaLastStep ? areaStepsCount : Math.max(0, activeStep - areaFirstStep);

      const areaCompleted = activeStep > areaLastStep;

      const percent =
        completedCount >= areaStepsCount ? 100 : (completedCount / areaStepsCount) * 100;

      const areaFlexColumn = areaStepsCount;
      const isAreaActivated = activeStep >= areaFirstStep;

      stepCount += areaStepsCount;

      return {
        areaFirstStep,
        areaLastStep,
        areaStepsCount,
        completedCount,
        areaCompleted,
        percent,
        areaFlexColumn,
        isAreaActivated,
      };
    });
  }, [stepsConfig, activeStep]);
}
